import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { getEmailsTypesData } from 'redux/reducers/emails/selectors';
import { TEmailTypeItem } from 'redux/reducers/emails/types';

const uniqueByHandle = (items: TEmailTypeItem[]): TEmailTypeItem[] => {
	const seen = new Set<string>();
	return items.filter((item) => {
		if (seen.has(item.handle)) return false;
		seen.add(item.handle);
		return true;
	});
};

export const TypeHandleFilter = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const navigateParams = queryString.parse(location.search);
	const emailTypes = useSelector(getEmailsTypesData);

	const options = useMemo(() => uniqueByHandle(emailTypes ?? []), [emailTypes]);

	const descriptionCounts = useMemo(() => {
		const counts = new Map<string, number>();
		options.forEach((item) => {
			const key = item.description ?? item.handle;
			counts.set(key, (counts.get(key) ?? 0) + 1);
		});
		return counts;
	}, [options]);

	const selectedHandle = navigateParams.SearchTypeHandle as string | undefined;
	const selectedOption: TEmailTypeItem | null =
		selectedHandle && emailTypes?.length
			? (emailTypes.find((t) => t.handle === selectedHandle) ?? null)
			: null;

	const handleChange = (_: unknown, value: TEmailTypeItem | null) => {
		const next = { ...navigateParams };
		if (value?.handle) {
			next.SearchTypeHandle = value.handle;
		} else {
			delete next.SearchTypeHandle;
		}
		navigate({ pathname: location.pathname, search: queryString.stringify(next) });
	};

	return (
		<Autocomplete<TEmailTypeItem, false>
			size="small"
			sx={{
				width: '400px',
				mr: 1,
				'& .MuiOutlinedInput-notchedOutline': {
					border: '1px solid #016193',
				},
			}}
			options={options}
			getOptionLabel={(option) => {
				const base = option.description ?? option.handle;
				const count = descriptionCounts.get(base) ?? 0;
				return count > 1 ? `${base} (${option.handle})` : base;
			}}
			value={selectedOption}
			onChange={handleChange}
			// renderInput={(params) => <TextField {...params} label="Type" />}
			isOptionEqualToValue={(option, value) => option.handle === value?.handle}
			clearOnBlur={false}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="outlined"
					sx={{
						ml: 1,
						flex: 1,
						fontSize: '.9rem',
						height: '28px',
						p: '0px 0px',
						'& input': {
							padding: '0px 0px',
							margin: '0px',
							fontSize: '.8rem',
							height: '11px!important',
							color: '#016193',
							fontWeight: 'bold',
							'&::placeholder': {
								fontSize: '.8rem',
								fontWeight: 'normal',
							},
						},
					}}
					placeholder="Type"
					size="small"
				/>
			)}
		/>
	);
};
